import { useCallback, useEffect, useState } from "react"

export interface ScrollFadeState {
  /** True when there is scrollable content hidden to the left. */
  fadeStart: boolean
  /** True when there is scrollable content hidden to the right. */
  fadeEnd: boolean
}

/**
 * Computes which edges of a horizontally-scrollable element
 * should fade. A bare overflow-x strip gives no hint that it
 * can scroll (especially on iOS, where there's no visible
 * scrollbar), so callers paint a `mask-image` gradient on
 * whichever edge still has hidden content.
 *
 * Returns the live state plus a callback ref to attach to the
 * scroll container. We use a callback ref (not `useRef`) so the
 * first measurement runs the moment the node mounts.
 */
export function useScrollFade() {
  const [node, setNode] = useState<HTMLElement | null>(null)
  const [state, setState] = useState<ScrollFadeState>({
    fadeStart: false,
    fadeEnd: false,
  })

  const measure = useCallback((el: HTMLElement) => {
    const { scrollLeft, scrollWidth, clientWidth } = el
    // 1px slack absorbs sub-pixel rounding so we don't paint a
    // hairline fade when the strip is actually at its edge.
    const maxScroll = scrollWidth - clientWidth
    const fadeStart = scrollLeft > 1
    const fadeEnd = scrollLeft < maxScroll - 1
    // Bail when nothing changed so a horizontal scroll that stays
    // mid-strip doesn't re-render the chip group on every frame.
    setState((prev) =>
      prev.fadeStart === fadeStart && prev.fadeEnd === fadeEnd
        ? prev
        : { fadeStart, fadeEnd },
    )
  }, [])

  useEffect(() => {
    if (!node) return

    const onScroll = () => measure(node)
    onScroll()

    node.addEventListener("scroll", onScroll, { passive: true })

    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onScroll)
      observer.observe(node)
    }

    return () => {
      node.removeEventListener("scroll", onScroll)
      observer?.disconnect()
    }
  }, [node, measure])

  return { ref: setNode, ...state }
}

/**
 * Maps a {@link ScrollFadeState} to the `mask-image` CSS value
 * that fades the active edges. Returns `undefined` when neither
 * edge needs a fade so the element keeps its normal painting.
 */
export function scrollFadeMask({
  fadeStart,
  fadeEnd,
}: ScrollFadeState): string | undefined {
  if (!fadeStart && !fadeEnd) return undefined

  const left = fadeStart ? "transparent" : "black"
  const right = fadeEnd ? "transparent" : "black"

  return (
    `linear-gradient(to right, ${left} 0, ` +
    `black 24px, black calc(100% - 24px), ${right} 100%)`
  )
}
